import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { useTheme } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Pages
import Dashboard from './pages/Dashboard';
import VisitorsList from './pages/VisitorsList';
import VisitorDetails from './pages/VisitorDetails';
import CheckIn from './pages/CheckIn';
import CheckOut from './pages/CheckOut';
import PreRegister from './pages/PreRegister';
import UsersList from './pages/UsersList';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import RoleManagement from './pages/RoleManagement';
import PurposeManagement from './pages/PurposeManagement';
import Login from './pages/Login';
import Register from './pages/Register';
import NotFound from './pages/NotFound';

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

function App() {
  const { theme } = useTheme();
  
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Routes>
          {/* Auth routes */}
          <Route path="/" element={<AuthLayout />}>
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
          </Route>
          
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="visitors" element={<VisitorsList />} />
            <Route path="visitors/:id" element={<VisitorDetails />} />
            <Route path="check-in" element={<CheckIn />} />
            <Route path="check-out/:id" element={<CheckOut />} />
            <Route path="pre-register" element={<PreRegister />} />
            <Route path="users" element={<UsersList />} />
            <Route path="profile" element={<Profile />} />
            <Route path="settings" element={<Settings />} />
            <Route path="roles" element={<RoleManagement />} />
            <Route path="purposes" element={<PurposeManagement />} />
          </Route>
          
          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </MuiThemeProvider>
  );
}

export default App;
